import { Routes, Route } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { useIsAuthenticated } from './hooks';
import {
  AccountDetails,
  AccountStatement,
  AccountSummary,
  AccessControl,
  ActivityLog,
  ActivityLogs,
  AffiliateListing,
  AffiliateReq,
  AllDownlineListing,
  BankerDashboard,
  BankerDetail,
  Bankers,
  BannerManagement,
  BetList,
  BetSetting,
  BussinessReports,
  CasinoBetList,
  CasinoControl,
  CasinoDetail,
  CasinoPlByDownline,
  CasinoPlByPlayer,
  CategoryControl,
  ComissionReport,
  AffiliateActiveUserOfWeek,
  ContentManagement,
  DashBoard,
  DashboardPage,
  DWRequest,
  EventManagement,
  Export,
  FilteredGames,
  GameControl,
  InactiveMemerList,
  Login,
  ManageAccount,
  NotFoundPage,
  NotificationManagement,
  OnboardingPage,
  PlByEvent,
  PlByPlayer,
  PlDownline,
  ProviderControl,
  Risk,
  Setting,
  SubUsers,
  SubUsersListing,
  SupportContactManagement,
  TransactionCode,
  TransactionHistory,
  UserAccountStatement,
  UserBetHistory,
  UserBettingPL,
  RoleWiseAccountStatement,
  Roles,
  RolesAndPrivileges,
  CryptoConversion,
  BonusStatement,
  PolicyManagement,
  RiskDetailsPage,
  Settlement,
  AddPromotion,
  CreateRoles,
} from '@/components/routes';
import AffiliateSetting from './pages/AffiliateManagement/AffiliateSetting';
import { useRoleCheck } from './hooks/useRoleCheck';
import ProtectedAdminRoutes from './components/routes/ProtectedAdminRoutes';
import ModuleRoute from './components/ModuleRoute';
import { PermissionProvider } from './context/PermissionContext';
import { Permission } from './types/rolesTypes';
import { useGetUserRolesDetailsQuery } from './store/api/rolesApi';
import { skipToken } from '@reduxjs/toolkit/query';

export function App() {
  const isAuth = useIsAuthenticated();

  const user = useSelector((state: RootState) => state.user);
  const userId = user?.data?.id;
  const userRole = user?.data?.role?.name;
  const { data, isLoading } = useGetUserRolesDetailsQuery(userId ? Number(userId) : skipToken, {
    skip: !userId,
  });
  const userData = data?.data?.[0];

  const { isOwner, isRoleUser } = useRoleCheck();
  const ownerOnly = (element: React.ReactNode) => (
    <ProtectedAdminRoutes allowed={isOwner || isRoleUser}>{element}</ProtectedAdminRoutes>
  );
  return (
    <PermissionProvider user={userData as Permission} isLoading={isLoading}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<RegisterPage />} /> */}
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            {isAuth && userRole !== 'BANKER' ? (
              <>
                <Route
                  path="/"
                  element={
                    <ModuleRoute moduleName="Dashboard">
                      <DashboardPage />
                    </ModuleRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ModuleRoute moduleName="Dashboard">
                      <DashboardPage />
                    </ModuleRoute>
                  }
                />
              </>
            ) : (
              <>
                <Route path="/" element={<BankerDashboard />} />
                <Route path="/dashboard" element={<BankerDashboard />} />
              </>
            )}
            {/* <Route path="/profile" element={<ProfilePage />} /> */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Downline Routes */}
            <Route element={<ModuleRoute moduleName="Users" />}>
              <Route path="/members/:id" element={<AllDownlineListing />} />
              <Route path="/members/:id/:role/:user/:memberid/:upline" element={<SubUsers />} />
              <Route path="/settlement" element={<Settlement />} />
            </Route>
            <Route
              path="/members/:id/sub/:memberid/:path/:userType"
              element={
                <ModuleRoute moduleName="Users" requiredAccess={['read']}>
                  <AccountDetails />
                </ModuleRoute>
              }
            >
              <Route index element={<AccountSummary />} />
              <Route
                path="account-summary"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'update']}>
                    <AccountSummary />
                  </ModuleRoute>
                }
              />

              <Route
                path="account-statement"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserAccountStatement />
                  </ModuleRoute>
                }
              />
              <Route
                path="bet-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserBetHistory />
                  </ModuleRoute>
                }
              />
              <Route
                path="betting-pl"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserBettingPL />
                  </ModuleRoute>
                }
              />

              <Route
                path="casino-detail"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <CasinoDetail />
                  </ModuleRoute>
                }
              />

              <Route
                path="transaction-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <TransactionHistory />
                  </ModuleRoute>
                }
              />
              <Route
                path="login-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <ActivityLog />
                  </ModuleRoute>
                }
              />
            </Route>

            {/* Affiliate Management */}
            <Route path="/affiliate-dashboard" element={<DashBoard />} />
            <Route path="/affiliate-req" element={<AffiliateReq />} />
            <Route path="/affiliate-list" element={<AffiliateListing />} />
            <Route path="/affiliate-list/:id" element={<SubUsersListing />} />
            <Route path="/affiliate-comission-report" element={<ComissionReport />} />
            <Route path="/affiliate-comission-report/:id" element={<AffiliateActiveUserOfWeek />} />
            <Route path="/affiliate-setting" element={<AffiliateSetting />} />

            {/* Change Password Routes */}
            <Route path="/settings" element={<Setting />} />

            {/* Event Management*/}
            <Route element={<ModuleRoute moduleName="EventManagement" />}>
              <Route path="/event-management/:status?" element={ownerOnly(<EventManagement />)} />
              <Route path="/bet-setting" element={ownerOnly(<BetSetting />)} />
            </Route>
            {/* Casino management */}
            <Route element={<ModuleRoute moduleName="CasinoControl" />}>
              <Route path="/casino-management" element={ownerOnly(<CasinoControl />)}>
                <Route index element={<CategoryControl />} />
                <Route path="provider" element={<ProviderControl />} />
                <Route path="game" element={<GameControl />} />
              </Route>
              <Route path="/casino-management/games" element={ownerOnly(<FilteredGames />)} />
            </Route>
            {/* Content Management */}
            <Route element={<ModuleRoute moduleName="ContentManagement" />}>
              <Route path="/content-management" element={ownerOnly(<ContentManagement />)}>
                <Route index element={<BannerManagement />} />
                <Route path="notification-management" element={<NotificationManagement />} />
                <Route path="support-management" element={<SupportContactManagement />} />
              </Route>
            </Route>
            <Route path="/add-promotion" element={<AddPromotion />} />
            {/* Bonus Management */}
            <Route element={<ModuleRoute moduleName="Bonus" />}>
              <Route path="/bonus">
                <Route path="policy-management" element={ownerOnly(<PolicyManagement />)} />
                <Route path="statement" element={ownerOnly(<BonusStatement />)} />
              </Route>
            </Route>
            {/* Banker Management */}
            <Route path="/bankers" element={ownerOnly(<Bankers />)} />
            <Route path="/bankers/:id/:details?/:mode?/:type?" element={<BankerDetail />} />
            <Route element={<ModuleRoute moduleName="BankingManagement" />}>
              <Route path="/requests/:mode?/:type?" element={ownerOnly(<DWRequest />)} />
              <Route path="/manage-account/:type?" element={ownerOnly(<ManageAccount />)} />
              <Route path="/crypto-conversion" element={ownerOnly(<CryptoConversion />)} />
            </Route>
            <Route path="/account-statement" element={<AccountStatement />} />
            <Route path="/banker-dashboard" element={<BankerDashboard />} />
            <Route
              path="/rolewise-account-statement"
              element={
                <ModuleRoute moduleName="Account Statement">
                  <RoleWiseAccountStatement />
                </ModuleRoute>
              }
            />

            {/* Risk Management Routes */}
            <Route element={<ModuleRoute moduleName="RiskManagement" />}>
              <Route path="/risk" element={<Risk />} />
              <Route path="/risk-details" element={<RiskDetailsPage />} />
            </Route>

            {/* Reports Routes */}
            <Route element={<ModuleRoute moduleName="MyReport" />}>
              <Route path="/bet-list" element={<BetList />} />
              <Route path="/casino-bet-list" element={<CasinoBetList />} />
              <Route path="/pl-by-downline" element={<PlDownline />} />
              <Route path="/pl-by-player" element={<PlByPlayer />} />
              <Route path="/pl-by-event" element={<PlByEvent />} />
              <Route path="/casinopl-by-player" element={<CasinoPlByPlayer />} />
              <Route path="/casinopl-by-downline" element={<CasinoPlByDownline />} />
              <Route path="/business-reports" element={<BussinessReports />} />
            </Route>
            <Route path="/inactive-members" element={<InactiveMemerList />} />
            {/* activity logs */}
            <Route
              path="/activity-logs"
              element={
                <ModuleRoute moduleName="Activity Logs">
                  <ActivityLogs />
                </ModuleRoute>
              }
            />

            {/* Export History */}
            <Route
              path="/export"
              element={
                <ModuleRoute moduleName="Export History">
                  <Export />
                </ModuleRoute>
              }
            />
            {/* Roles And Privileges */}
            <Route element={<ModuleRoute moduleName="Roles Privileges" />}>
              <Route path="/roles-and-privileges" element={<RolesAndPrivileges />}>
                <Route index element={<Roles />} />
                <Route path="access" element={<AccessControl />} />
              </Route>
              <Route
                path="create-roles/:id?/:type?"
                element={
                  <ModuleRoute moduleName="Roles Privileges" requiredAccess={['read', 'update']}>
                    <CreateRoles />
                  </ModuleRoute>
                }
              />
            </Route>
          </Route>
        </Route>
        {/* </Route> */}
        <Route path="/transaction-code" element={<TransactionCode />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PermissionProvider>
  );
}
